import { useState } from "react";
import { API_BASE } from "@/lib/api";
import { Download, FileBarChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const RANGES = [
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "custom", label: "Custom range" },
];

export default function Reports() {
  const [range, setRange] = useState("this_month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (range === "custom" && (!start || !end)) {
      return toast.error("Please pick a start and end date.");
    }
    setBusy(true);
    try {
      const token = localStorage.getItem("jt_token");
      const q = new URLSearchParams({ range });
      if (range === "custom") { q.set("start", start); q.set("end", end); }
      const url = `${API_BASE}/reports/export?${q.toString()}&auth=${encodeURIComponent(token || "")}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `jobticker-${range}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Report downloaded");
    } catch { toast.error("Could not download report"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h1 className="font-display font-bold text-3xl text-slate-900">Reports</h1>
        <p className="text-sm text-slate-500 mt-1">Export a CSV snapshot of tickets for any time range.</p>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 p-6 space-y-5">
        <div className="flex flex-wrap gap-1.5">
          {RANGES.map((r) => (
            <button key={r.key} data-testid={`range-${r.key}`} onClick={() => setRange(r.key)}
              className={`h-9 px-3 rounded-full text-xs font-semibold border transition-colors ${
                range === r.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}>
              {r.label}
            </button>
          ))}
        </div>

        {range === "custom" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Start date</Label><Input type="date" data-testid="range-start" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1.5 h-11" /></div>
            <div><Label>End date</Label><Input type="date" data-testid="range-end" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1.5 h-11" /></div>
          </div>
        )}

        <div className="rounded-xl bg-indigo-50/50 border border-indigo-100 p-4 flex items-start gap-3">
          <FileBarChart className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-700">
            <div className="font-semibold text-slate-900">What's in the CSV?</div>
            Every ticket in range with type, priority, status, outlet, creator, timestamps, reopen count and overdue flag.
          </div>
        </div>

        <Button onClick={download} disabled={busy} data-testid="report-download" className="h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold">
          <Download className="w-4 h-4 mr-1.5" /> Download CSV
        </Button>
      </div>
    </div>
  );
}
